/**
 * @file BannerCard.tsx
 * @brief Flush list row for a home-screen promotional banner.
 *
 * @details
 * Image preview, copy summary, status badge, and edit affordance.
 * Renders as a flat grid row (no card chrome) for GridBlock lists.
 *
 * @author DES
 * @date 07-06-2026
 * @updated 29-07-2026 — flush GridCell row layout
 */

import { ChevronRight, EyeOff, Link2, ListOrdered } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import type { Banner } from "../schemas";

interface BannerCardProps {
  banner: Banner;
  onEdit: (banner: Banner) => void;
}

export function BannerCard({ banner, onEdit }: BannerCardProps) {
  return (
    <button
      type="button"
      onClick={() => onEdit(banner)}
      className="flex w-full items-center gap-4 px-4 py-4 text-left transition-colors hover:bg-muted/30 md:px-5"
    >
      <div className="relative h-20 w-28 shrink-0 overflow-hidden bg-muted">
        <img
          src={banner.image_url}
          alt={banner.title ?? "Banner"}
          className="h-full w-full object-cover"
        />
        {!banner.is_enabled ? (
          <div className="absolute inset-0 flex items-center justify-center bg-black/45">
            <EyeOff className="h-5 w-5 text-white" />
          </div>
        ) : null}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="truncate text-sm font-semibold">
            {banner.title || "Untitled banner"}
          </p>
          <Badge variant={banner.is_enabled ? "default" : "muted"}>
            {banner.is_enabled ? "Active" : "Disabled"}
          </Badge>
        </div>
        <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
          {banner.subtitle || "No subtitle"}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <ListOrdered className="h-3 w-3" />
            Order {banner.display_order}
          </span>
          {banner.cta_text ? (
            <span className="inline-flex items-center gap-1">
              <Link2 className="h-3 w-3" />
              {banner.cta_text}
            </span>
          ) : null}
        </div>
      </div>

      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8 shrink-0"
        aria-label={`Edit ${banner.title ?? "banner"}`}
        onClick={(e) => {
          e.stopPropagation();
          onEdit(banner);
        }}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </button>
  );
}
